import { useCallback } from 'react';

import { useAccount } from 'wagmi';

import { ILoanRequestDetail } from '@/redux/loans/model';
import { AddressType } from 'types';

import { useAppSelector } from './redux';

type ILoanRequest = {
  senderId: ILoanRequestDetail['borrower']['id'] | undefined;
  lenderAddress?: ILoanRequestDetail['ownerRequest']['lenderAddress'];
  ownerAddress: AddressType | undefined;
};

type TCheckLenderPayload = { lenderAddress: string; senderId: number };

type UseUserBorrowerReturn<T extends boolean | undefined> = T extends boolean ? { checkIsBorrower: (loanRequest: ILoanRequest) => boolean | null; checkIsLender: (loanRequest: TCheckLenderPayload) => boolean | null } : boolean | null;

const useUserBorrower = <T extends boolean | undefined = undefined>(active?: boolean, checkWithFn?: T): UseUserBorrowerReturn<T> => {
  const loan = useAppSelector((state) => (!active ? state.loans.loan : state.loans.activeLoan));
  const user = useAppSelector((state) => state.user.userDetail);

  const { address } = useAccount();

  const checkIsBorrower = useCallback(
    (loanRequest: ILoanRequest) => {
      const isAddressSame = loanRequest.ownerAddress?.toLowerCase() === address?.toLowerCase();

      if (!user) return null;

      if (user.id === loanRequest.senderId) {
        if (isAddressSame) {
          return true;
        }
        return null;
      }
      if (isAddressSame) {
        return null;
      }
      return false;
    },
    [address, user]
  );

  const checkIsLender = useCallback(
    (loanRequest: TCheckLenderPayload) => {
      const isAddressSame = loanRequest.lenderAddress?.toLowerCase() === address?.toLowerCase();
      if (!user) return null;

      if (user.id === loanRequest.senderId) {
        if (isAddressSame) {
          return true;
        }
        return null;
      }
      if (isAddressSame) {
        return null;
      }
      return false;
    },
    [address, user]
  );

  if (checkWithFn) {
    return { checkIsBorrower, checkIsLender } as UseUserBorrowerReturn<T>;
  }

  if (!loan) return null as UseUserBorrowerReturn<T>;

  if ('borrower' in loan) {
    return checkIsBorrower({ ...loan, senderId: loan.borrower.id }) as UseUserBorrowerReturn<T>;
  }

  return (loan.loanRequest.loanTokenOwner.ownerAddress === address) as UseUserBorrowerReturn<T>;
};

export { useUserBorrower };
